import { EXTRACT_OCR_STATUS, TRACK_EVENTS } from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { onPremHttpRequest } from "core/model/careseekers";
import { isFileHISStateful, isFileV2 } from "core/model/files/utils";
import { getErrorMessage } from "core/model/utils/errors";
import {
  ExtractOCRResult,
  ExtractOCRStatus,
  ExtractOCRStatusResponse,
  FileCategory,
  FileState,
} from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useHandleFileDownload } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import { useCallback, useEffect, useState } from "react";
import { useTracking } from "react-tracking";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { useTranslations } from "translations";

type OCRResultsByFileId = Record<string | number, ExtractOCRResult>;

export const useExtractOCR = ({
  auctionId,
  careseekerId,
  consultationId,
  externalId,
  ocrStatus,
  ocrStatusError,
  onComplete,
  patientId,
}: {
  careseekerId: number | undefined;
  patientId: number;
  auctionId: number | undefined;
  consultationId: number | undefined;
  externalId: string | undefined;
  onComplete: () => Promise<ExtractOCRStatusResponse | null>;
  ocrStatus: ExtractOCRStatusResponse | null;
  ocrStatusError: string | null;
}) => {
  const [results, setResults] = useState<OCRResultsByFileId>({});
  const [errors, setErrors] = useState<Array<Record<string, string>>>([]);
  const [extractingAllFiles, setExtractingAllFiles] = useState(false);
  const toast = useToast();
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const careseeker = useLoggedCareseeker();

  const [processingFileIds, setProcessingFileIds] = useState<
    Array<string | number>
  >([]);

  const handleDownload = useHandleFileDownload({
    externalId: externalId ?? "",
    target: undefined,
  });

  const handleOcrForFile = useCallback(
    async ({
      file,
      fileCategory,
      id,
    }: {
      file: File;
      fileCategory: FileCategory;
      id: number;
    }) => {
      if (ocrStatusError) {
        return;
      }

      setProcessingFileIds((prev) => [...prev, id]);

      let ocrResult: ExtractOCRResult | undefined;
      try {
        if (!careseekerId || !patientId) {
          throw new Error("careseekerId or patientId is not defined");
        }
        if (!auctionId && !consultationId) {
          throw new Error("auctionId or consultationId is not defined");
        }
        const result = await aiApi.extract.ocr({
          auctionId,
          careseekerId,
          consultationId,
          file,
          fileCategory,
          fileId: String(id),
          patientId,
        });

        ocrResult = result;
        setResults((prev) => ({
          ...prev,
          [id]: result,
        }));
      } finally {
        setProcessingFileIds((prev) => prev.filter((fileId) => fileId !== id));
      }

      if (ocrResult != null) {
        await onComplete();
      }

      return ocrResult;
    },
    [
      auctionId,
      consultationId,
      ocrStatusError,
      setProcessingFileIds,
      setResults,
      onComplete,
    ],
  );

  const downloadAndRunOcr = useCallback(
    async (file: FileState): Promise<ExtractOCRResult> => {
      if (!careseekerId || !patientId) {
        throw new Error("careseekerId or patientId is not defined");
      }

      if (!auctionId && !consultationId) {
        throw new Error("auctionId or consultationId is not defined");
      }

      const downloadResult = await handleDownload(file);
      if (!downloadResult) {
        throw new Error("Failed to download file - no result returned");
      }
      if (!downloadResult.pdfBytes) {
        throw new Error("No PDF bytes returned from download");
      }

      const ocrResult = await aiApi.extract.ocr({
        auctionId,
        careseekerId,
        consultationId,
        file: new File(
          [downloadResult.pdfBytes as BlobPart],
          downloadResult.fileName,
          {
            type: "application/pdf",
          },
        ),
        fileCategory: isFileHISStateful(file)
          ? file.document_category_number
          : file.category,
        fileId: String(file.id),
        patientId,
      });

      setResults((prev) => ({
        ...prev,
        [file.id]: ocrResult,
      }));

      return ocrResult;
    },
    [auctionId, consultationId, careseekerId, patientId, handleDownload],
  );

  const handleDownloadAndRunOcr = useCallback(
    async ({
      extractAllFiles,
      files,
    }: {
      files: FileState[];
      extractAllFiles?: boolean;
    }) => {
      // OCR status polling can fail while manual extraction still works — do not
      // block here (see useExtractOCRStatus initial-load error handling).
      const excludedStatuses = [
        EXTRACT_OCR_STATUS.EXTRACTED,
        EXTRACT_OCR_STATUS.PROCESSING,
      ] as ExtractOCRStatus[];

      // get the documents that have already been extracted or are being processed for exclusion
      const excludedFileIds = ocrStatus?.documents
        .filter(
          (doc) => doc.recare_file_id && excludedStatuses.includes(doc.status),
        )
        .map((doc) => doc.recare_file_id);

      const filesToExtract = files.filter((file) => {
        // only extract non-HIS files
        if (!isFileV2(file) || file.kis_document_id_string) return false;

        // if the file is excluded by its status, skip (coerce ids — API may return strings)
        return !excludedFileIds?.some(
          (excludedId) => String(excludedId) === String(file.id),
        );
      });
      if (!filesToExtract.length) return;

      const hisFiles = files.filter(isFileHISStateful);

      trackEvent({
        extracted_files: String(excludedFileIds?.length),
        his_files: String(hisFiles.length),
        name: TRACK_EVENTS.EXTRACT_ALL_FILES,
        total_files: String(files.length),
      });

      setErrors([]);
      setExtractingAllFiles(!!extractAllFiles);
      setProcessingFileIds((prev) => [
        ...prev,
        ...filesToExtract.map((f) => f.id),
      ]);

      let completedWithoutThrow = false;
      try {
        await Promise.all(
          filesToExtract.map(async (file) => {
            try {
              await downloadAndRunOcr(file);
            } catch (err) {
              const message = getErrorMessage(err);
              console.error(
                `error with extract ocr for file ${file.id}`,
                message,
              );
              toast({
                color: "danger",
                message: translations.general.error,
              });
              setErrors((prev) => [
                ...prev,
                { fileId: String(file.id), message },
              ]);
            }
          }),
        );
        completedWithoutThrow = true;
      } finally {
        setProcessingFileIds((prev) =>
          prev.filter((id) => !filesToExtract.some((file) => file.id === id)),
        );
        setExtractingAllFiles(false);
      }

      if (completedWithoutThrow) {
        await onComplete();
      }
    },
    [downloadAndRunOcr, onComplete, ocrStatus],
  );

  const handleHISAIPush = useCallback(
    async ({ files }: { files: FileState[] }) => {
      if (!externalId) {
        console.error("handleHISAIPush: externalId is required");
        return;
      }

      const { on_premise_authorization_token, on_premise_domain_ssl } =
        careseeker?.config ?? {};

      if (!on_premise_domain_ssl) {
        console.error("handleHISAIPush: on_premise_domain_ssl is required");
        return;
      }

      const hisFiles = files.filter(isFileHISStateful);
      if (!hisFiles.length) return;

      setProcessingFileIds((prev) => [...prev, ...hisFiles.map((f) => f.id)]);

      try {
        const response = await onPremHttpRequest({
          method: "POST",
          on_premise_authorization_token,
          on_premise_domain_ssl,
          path: `patient/${externalId}/ai-push`,
        });

        if (!response.ok) {
          throw new Error(`ai-push failed with status ${response.status}`);
        }

        await onComplete();
      } catch (err) {
        console.error("handleHISAIPush error:", getErrorMessage(err));
        toast({ color: "danger", message: translations.general.error });
      } finally {
        setProcessingFileIds((prev) =>
          prev.filter((id) => !hisFiles.some((f) => f.id === id)),
        );
      }
    },
    [externalId, careseeker, onComplete, toast, translations],
  );

  const handleExtractAll = useCallback(
    async ({ files }: { files: FileState[] }) => {
      await handleDownloadAndRunOcr({ extractAllFiles: true, files });
      const hisFiles = files.filter(isFileHISStateful);
      if (hisFiles.length > 0) {
        await handleHISAIPush({ files: hisFiles });
      }
    },
    [handleDownloadAndRunOcr, handleHISAIPush],
  );

  useEffect(() => {
    return () => {
      setProcessingFileIds([]);
      setErrors([]);
      setExtractingAllFiles(false);
    };
  }, []);

  return {
    errors,
    extractingAllFiles,
    handleDownloadAndRunOcr,
    handleExtractAll,
    handleHISAIPush,
    handleOcrForFile,
    processingFileIds,
    results,
  };
};

export type UseExtractOCRReturn = ReturnType<typeof useExtractOCR>;
